import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  InputAdornment,
  CircularProgress,
  Typography,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import authApi from "Service/Auth.api";
import { useDispatch, useSelector } from "react-redux";
import { setAlertData } from "../../Redux/Screen/Action";
import { clearUser } from "../../Redux/User/Action";
import { UserInitialStateType } from "DataTypes/Redux";
import { safeNavigate } from "Utils/Common";

interface ChangePasswordModalProps {
  open: boolean;
  onClose: () => void;
}

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const { myInfo }: UserInitialStateType = useSelector((state: any) => state?.user);

  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    if (loading) return;
    setOldPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setError(null);
    onClose();
  };

  const validate = () => {
    if (!oldPassword) return "Old password is required.";
    if (!newPassword) return "New password is required.";
    if (newPassword.length < 6) return "New password must be at least 6 characters long.";
    if (newPassword !== confirmPassword) return "New passwords do not match.";
    if (oldPassword === newPassword) return "New password must be different from old password.";
    return null;
  };

  const handleSubmit = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    if (!myInfo?.id) {
      setError("User information not found.");
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const payload = {
        UserId: Number(myInfo.id),
        OldPassword: oldPassword,
        NewPassword: newPassword,
      };

      const res = await authApi.changePassword(payload);
      
      if (res?.success) {
        dispatch(setAlertData({
          openAlert: true,
          alertMessage: "Password changed successfully. Please log in again.",
          alertVariant: "success",
        }));
        handleClose();
        
        // Log out the user
        localStorage.clear();
        dispatch(clearUser() as any);
        safeNavigate("/login");
      } else {
        const errorList = res?.data?.errors || res?.data?.Errors;
        let errorMessage = res?.alert?.alertMessage || res?.data?.message || res?.data?.Message || "Failed to change password.";
        
        if (Array.isArray(errorList) && errorList.length > 0) {
          errorMessage = errorList.join(", ");
        }
        
        throw new Error(errorMessage);
      }
    } catch (err: any) {
      console.error(err);
      setError(err?.message || err?.Message || "An error occurred while changing password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600, color: "#1a237e" }}>Change Password</DialogTitle>
      <DialogContent>
        {error && (
          <Typography color="error" sx={{ mb: 2, fontSize: "0.9rem" }}>
            {error}
          </Typography>
        )}
        <TextField
          label="Old Password"
          type={showOldPassword ? "text" : "password"}
          fullWidth
          margin="normal"
          value={oldPassword}
          onChange={(e) => setOldPassword(e.target.value)}
          disabled={loading}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowOldPassword(!showOldPassword)} edge="end">
                  {showOldPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          label="New Password"
          type={showNewPassword ? "text" : "password"}
          fullWidth
          margin="normal"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          disabled={loading}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowNewPassword(!showNewPassword)} edge="end">
                  {showNewPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          label="Confirm New Password"
          type={showConfirmPassword ? "text" : "password"}
          fullWidth
          margin="normal"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          disabled={loading}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                  {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button onClick={handleClose} disabled={loading} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={loading}
          variant="contained"
          sx={{
            background: "linear-gradient(90deg, #1a237e 0%, #283593 100%)",
            color: "white",
            fontWeight: 600,
            px: 4,
            "&:hover": {
              background: "linear-gradient(90deg, #283593 0%, #3949ab 100%)",
            },
          }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : "Submit"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChangePasswordModal;
